import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-fees-explained',
  title: 'Telegram Stars Fees Explained: What You Really Earn per Star',
  metaTitle: 'Telegram Stars Fees Explained (2026): Real Earnings per Star',
  metaDescription:
    'A clear breakdown of Telegram Stars fees: app store commission, Telegram revenue share, Fragment conversion, platform fees. Worked example of what a seller actually keeps.',
  keywords: [
    'telegram stars fees',
    'telegram stars commission',
    'telegram stars revenue share',
    'how much do developers earn from telegram stars',
    'telegram stars cost',
    'telegram stars payout rate',
  ],
  category: 'Business',
  datePublished: '2026-09-12',
  dateModified: '2026-10-11',
  readingTimeMin: 7,
  excerpt:
    'Fees around Telegram Stars are spread across several layers: the store where the buyer purchases Stars, the rate at which you convert Stars to TON, and any platform you use. This article untangles them with a worked example.',
  keyTakeaways: [
    'Telegram does not charge sellers a per-transaction fee for receiving Stars; the main costs are the buyer-side store commission and the Stars-to-TON conversion rate.',
    'Users typically pay about $0.015 to $0.025 per Star in-app, while sellers historically received about $0.013 per Star when converting to TON on Fragment.',
    'Telegram Stars Payment Platform charges a flat 3% with no setup, monthly or hidden fees, and lowers the minimum payout from 1,000 to 100 Stars.',
    'Compared with a 30% app store cut on a direct in-app purchase, Stars let digital sellers keep a significantly larger share of what buyers pay.',
  ],
  body: [
    h2('The four layers of Telegram Stars fees'),
    p('When someone pays you 100 Stars, the money has already travelled through several layers before it reaches your TON wallet. Understanding each one helps you price correctly.'),
    ol([
      '**Buyer-side purchase price**: what the user paid to acquire the Stars. Depends on where they bought them (App Store, Google Play, @PremiumBot or Fragment) and the pack size.',
      '**Store commission**: Apple and Google take their usual in-app purchase cut on Stars bought through the apps. This is baked into the Stars price; it does not appear on your side.',
      '**Stars-to-TON conversion**: when you withdraw, Fragment converts Stars to TON at a rate set by Telegram. This rate effectively defines your gross revenue per Star.',
      '**Platform fee (optional)**: if you use a payment platform instead of running your own bot, its fee applies. On Telegram Stars Payment Platform it is a flat 3%.',
    ]),

    h2('How much does a Telegram Star cost the buyer?'),
    p(
      'Stars are sold in packs, and bigger packs are cheaper per Star. In-app prices are roughly in the range of **$0.015 to $0.025 per Star**. On Fragment, where Stars are bought with TON and no store commission applies, the effective price is lower. Prices also vary by country because the app stores localise them.',
    ),

    h2('How much does the seller receive per Star?'),
    p(
      'Telegram has historically converted Stars to TON at a rate equivalent to **about $0.013 per Star**, adjusted for the TON price at the time of withdrawal. This number is the key figure for your business: it means that, roughly, a product priced at 100 Stars brings in around $1.30 before any platform fee.',
    ),
    callout(
      'Rates are not contractual and can change. Check the live Stars rate on [fragment.com/stars](https://fragment.com/stars) before setting long-term prices, and read our [regularly updated price guide](/blog/telegram-stars-price-usd-ton).',
    ),

    h2('Worked example: a 500 Stars course'),
    p('Say you sell an online course for 500 Stars through a payment link on Telegram Stars Payment Platform.'),
    table(
      ['Step', 'Amount'],
      [
        ['Buyer pays (in-app, mid-size pack)', '≈ $10 for 500 Stars'],
        ['Credited to your balance', '500 Stars'],
        ['Platform fee (3%)', '15 Stars'],
        ['Net Stars for withdrawal', '485 Stars'],
        ['Converted to TON at ≈ $0.013/Star', '≈ $6.30 in TON'],
      ],
    ),
    p(
      'The gap between what the buyer paid and what you receive is mostly the app store commission and Telegram\'s share, not the platform. For comparison, selling the same course as a $10 in-app purchase in your own iOS app would leave you about $7 after the 30% store cut, but you would also have to build and maintain the app, the checkout and the payout logic.',
    ),

    h2('Telegram Stars Payment Platform pricing'),
    ul([
      '**3% per transaction**, applied once at withdrawal. No fee if you do not get paid.',
      '**No setup fee, no monthly fee, no minimum volume.**',
      '**Minimum payout: 100 Stars** to any TON wallet (Fragment requires 1,000).',
      '**Included**: unlimited payment links, redirect links with tracking, real-time ledger, dashboard, REST API and webhooks, 13 interface languages.',
    ]),
    p('See the [pricing section on our home page](/#pricing) or [why merchants choose us](/why).'),

    h2('Fees compared with other ways to get paid on Telegram'),
    table(
      ['Method', 'Typical cost to seller', 'Works for digital goods on mobile'],
      [
        ['Telegram Stars via platform', 'Conversion rate + 3%', 'Yes'],
        ['Telegram Stars via own bot', 'Conversion rate + your hosting/dev time', 'Yes'],
        ['Stripe through Bot Payments', '≈ 2.9% + $0.30 per transaction', 'No (physical goods and services only)'],
        ['Direct crypto transfer', 'Network fee', 'Grey area; no native checkout'],
        ['Own iOS/Android app with IAP', '15–30% store commission', 'Yes, but you build everything'],
      ],
    ),
    p('A fuller comparison is in [Telegram Stars vs Stripe, PayPal and crypto](/blog/telegram-stars-vs-stripe-paypal-crypto).'),

    h2('How to price your products in Stars'),
    ul([
      'Start from the **net revenue you want** per sale, divide by roughly $0.013, then add the platform fee and round up to a pack-friendly number (50, 100, 150, 250, 500, 1,000).',
      'Remember that buyers who already hold Stars perceive the price in Stars, not in dollars. Round numbers convert better.',
      'For recurring content, a Stars **subscription** at a lower monthly price often outperforms a one-off higher price.',
      'Test two price points with two payment links and compare conversions in the dashboard.',
    ]),

    h2('Hidden costs to watch'),
    ul([
      '**Refunds**: when you refund a buyer, the Stars go back to them in full; you lose the sale but there is no additional fee.',
      '**TON volatility**: your revenue is denominated in TON once withdrawn. Convert to stablecoins or fiat quickly if you want predictable income.',
      '**Exchange withdrawal fees**: converting TON to fiat on an exchange has its own small fees.',
    ]),
  ],
  faq: [
    {
      q: 'Does Telegram charge a fee for receiving Stars?',
      a: 'There is no explicit per-transaction fee charged to the seller. Telegram\'s share is reflected in the Stars-to-TON conversion rate applied when you withdraw.',
    },
    {
      q: 'What percentage do developers get from Telegram Stars?',
      a: 'Telegram has stated that developers receive the full value of Stars after app store fees. In practice that has corresponded to roughly $0.013 per Star when converting to TON, versus a buyer price of $0.015–$0.025.',
    },
    {
      q: 'What is the fee on Telegram Stars Payment Platform?',
      a: 'A flat 3% applied at withdrawal. There are no setup fees, monthly fees or minimum volumes.',
    },
    {
      q: 'Are Telegram Stars cheaper than Stripe for selling on Telegram?',
      a: 'For digital goods on mobile, Stars are the only compliant option, so the comparison is mostly theoretical. For services or physical goods, Stripe\'s 2.9% + $0.30 may be cheaper on large tickets but it pays in fiat and requires a business account.',
    },
  ],
  relatedSlugs: [
    'how-to-price-products-in-telegram-stars',
    'telegram-stars-price-usd-ton',
    'telegram-stars-vs-stripe-paypal-crypto',
    'withdraw-telegram-stars-to-ton',
  ],
};

export default article;
